import React, { createContext, useContext, useState, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { lazyWhenOpen } from '../components/lazyWhenOpen';

// R6 — the OTP sheet's code loads on first open.
const OtpSheet = lazyWhenOpen(() => import('../components/OtpSheet'));

const OtpGateContext = createContext(null);

export const OtpGateProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [state, setState] = useState({
    open: false,
    context: 'confirm',
    artistName: null,
    onSuccess: null,
  });

  // Ask for a verified customer. If already signed in as a customer, run the
  // action immediately; otherwise open the OTP sheet and continue on success.
  const requireCustomer = useCallback(
    (opts = {}) => {
      const { onSuccess } = opts;
      if (isAuthenticated && user?.role === 'customer') {
        onSuccess && onSuccess(user);
        return;
      }
      setState({
        open: true,
        context: opts.context || 'confirm',
        artistName: opts.artistName || null,
        onSuccess: onSuccess || null,
      });
    },
    [isAuthenticated, user]
  );

  // R2 — header "Log in": open the same sheet in place; stay on the page afterwards.
  const openLogin = useCallback(() => {
    setState({ open: true, context: 'login', artistName: null, onSuccess: null });
  }, []);

  const handleOpenChange = (o) => setState((s) => ({ ...s, open: o }));

  const handleVerified = (verifiedUser) => {
    const cb = state.onSuccess;
    setState((s) => ({ ...s, open: false, onSuccess: null }));
    if (cb) setTimeout(() => cb(verifiedUser), 60);
  };

  return (
    <OtpGateContext.Provider value={{ requireCustomer, openLogin }}>
      {children}
      <OtpSheet
        open={state.open}
        onOpenChange={handleOpenChange}
        onVerified={handleVerified}
        context={state.context}
        artistName={state.artistName}
      />
    </OtpGateContext.Provider>
  );
};

export const useOtpGate = () => {
  const ctx = useContext(OtpGateContext);
  if (!ctx) throw new Error('useOtpGate must be used within OtpGateProvider');
  return ctx;
};
